import { IExtendableDto } from "../../dto/customField/extendable-dto";
import { SortingEnum } from "../../enums";
import { RydeUiUnit } from "../../models";
import { ISearchCriteria } from "../../models/filters.models";
import {
  IGroupResult,
  IPaginatedResult,
  type IPaginationChangeEvent,
  type ISearchCriteriaGroupValue,
  type ITakeSkip,
} from "../../models/pagination.models";
import { isEntityServerGridTextSearchable } from "../common.helpers";
import {
  isGroupResult,
  isPaginatedResult,
  resolveFlatListTakeSkip,
} from "./pagination.helpers";

export type ComposedSearchPagination = ITakeSkip | ISearchCriteriaGroupValue[];

export interface IApplyGroupedServerSearchPaginationArgs {
  body: ISearchCriteria<IExtendableDto>;
  groupByField: string | null | undefined;
  paginationResult: ComposedSearchPagination;
  orderByField?: string;
  orderByDirection?: SortingEnum;
  groupPagination?: IPaginationChangeEvent;
  resetToFirstPage?: boolean;
  pageSize?: number;
}

export const applyGroupedServerSearchPaginationToBody = ({
  body,
  groupByField,
  paginationResult,
  orderByField,
  orderByDirection,
  groupPagination,
  resetToFirstPage,
  pageSize,
}: IApplyGroupedServerSearchPaginationArgs): void => {
  body.orderByField = orderByField;
  body.orderByDirection = orderByDirection;

  if (
    groupByField &&
    Array.isArray(paginationResult) &&
    paginationResult.length > 0
  ) {
    body.groupByField = groupByField;
    body.groupByValues = paginationResult;
    return;
  }

  const flatPage = resolveFlatListTakeSkip({
    paginationResult,
    groupPagination,
    resetToFirstPage,
    pageSize,
  });
  body.take = flatPage.take;
  body.skip = flatPage.skip;
};

export const applySimpleServerPaginationToBody = (
  body: ISearchCriteria<IExtendableDto>,
  paginationResult: ComposedSearchPagination,
): void => {
  if (!Array.isArray(paginationResult)) {
    body.take = paginationResult.take;
    body.skip = paginationResult.skip;
  }
};

export const applyServerGridTextSearchToBody = ({
  body,
  entity,
  searchValue,
}: {
  body: ISearchCriteria<IExtendableDto>;
  entity: RydeUiUnit;
  searchValue: string | undefined;
}): void => {
  if (!isEntityServerGridTextSearchable(entity) || !searchValue?.trim()) {
    return;
  }

  body.where = {
    ...body.where,
    search: {
      $ilike: searchValue,
    },
  };
};

export type ServerSearchPayload =
  | { kind: "groups"; groups: IGroupResult<IExtendableDto[]>[] }
  | { kind: "paginated"; items: IExtendableDto[]; count: number }
  | { kind: "array"; items: IExtendableDto[] };

export const classifyServerSearchPayload = (
  response: unknown,
): ServerSearchPayload | null => {
  if (isGroupResult(response)) {
    return {
      kind: "groups",
      groups: response as IGroupResult<IExtendableDto[]>[],
    };
  }

  if (isPaginatedResult(response)) {
    return {
      kind: "paginated",
      items: (response.items || []) as IExtendableDto[],
      count: response.count,
    };
  }

  if (Array.isArray(response)) {
    return { kind: "array", items: response as IExtendableDto[] };
  }

  return null;
};

export const rowsFromServerSearchPayload = (
  payload: unknown,
): IExtendableDto[] => {
  const parsed = classifyServerSearchPayload(payload);

  if (!parsed) {
    return [];
  }

  if (parsed.kind === "groups") {
    return parsed.groups.flatMap(group => group.groupData ?? []);
  }

  return parsed.items;
};

export type IPaginatedGridSearchPayload<T extends IExtendableDto> =
  T[] | IGroupResult<T[]>[] | IPaginatedResult<T[]>;
